/** Audio file validation — dependency-free, shared by every picker and drop. */

export const AUDIO_FILE_PICKER_MIME_TYPES: string[] = [
  'audio/*',
  'audio/mpeg',
  'audio/mp3',
  'audio/mp4',
  'audio/x-m4a',
  'audio/m4a',
  'audio/wav',
  'audio/x-wav',
  'audio/aac',
  'audio/ogg',
  'audio/flac',
  'audio/webm',
];

export { TRANSCRIPTION_AUDIO_MAX_BYTES } from '../upload/audio-limits';

/**
 * The web Audio form's cap (Groq's dev tier; larger needs batch transcription).
 * Mobile uploads up to {@link TRANSCRIPTION_AUDIO_MAX_BYTES}.
 */
export const AUDIO_UPLOAD_MAX_BYTES = 100 * 1024 * 1024;

const TRANSCRIBABLE_FILE_NAME = /\.(mp3|m4a|wav|flac|ogg|webm)$/i;

/**
 * Why the Audio form cannot transcribe a recording — `null` = it can. Stricter
 * than {@link isLikelyAudioFile}, which only says a file IS a recording.
 */
export function audioUploadProblem(file: {
  name: string;
  size?: number;
}): 'unsupported' | 'tooLarge' | null {
  if (!TRANSCRIBABLE_FILE_NAME.test(file.name)) return 'unsupported';
  return (file.size ?? 0) > AUDIO_UPLOAD_MAX_BYTES ? 'tooLarge' : null;
}

const AUDIO_EXTENSIONS = new Set([
  'mp3',
  'm4a',
  'wav',
  'aac',
  'ogg',
  'flac',
  'webm',
  'caf',
  'aiff',
  'aif',
  'opus',
  '3gp',
  'amr',
]);

/**
 * Validate that a picked file is likely an audio file.
 * Some OSes report generic or empty MIME for valid recordings.
 */
export function isLikelyAudioFile(
  mimeType: string | undefined | null,
  fileName: string,
): boolean {
  const m = (mimeType ?? '').toLowerCase().trim();
  if (m.startsWith('audio/')) return true;
  const base = fileName.split(/[/\\]/).pop() ?? '';
  const ext = base.includes('.') ? base.split('.').pop()!.toLowerCase() : '';
  return ext !== '' && AUDIO_EXTENSIONS.has(ext);
}
